import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { Store } from '@ngrx/store';
import { switchMap, take, filter, map } from 'rxjs/operators';

import { HttpService } from './http.service';
import {
  DEFAULT_POWER_PAGES_CURRENT_PAGE,
  DEFAULT_POWER_PAGES_PER_PAGE,
  PowerPage,
  PowerPageBlock,
  PowerPageBlockSavePayload,
  PowerPageDetail,
  PowerPageManageOptions,
  PowerPageSavePayload,
  PowerPagesListParams,
  PowerPagesListResponse,
} from '../../store/entities/models/power-page.model';
import { selectCurrentDomainId } from '../../store/entities/domains/domains.selectors';
import { transformCamelToSnake, transformSnakeToCamel } from '../../store/utils/transformers';

@Injectable({
  providedIn: 'root',
})
export class PowerPagesService {
  private http = inject(HttpService);
  private store = inject(Store);

  private getDomainId$(): Observable<string> {
    return this.store.select(selectCurrentDomainId).pipe(
      filter((id): id is string => id !== null),
      take(1)
    );
  }

  getPowerPagesList(params: PowerPagesListParams = {}): Observable<PowerPagesListResponse> {
    return this.getDomainId$().pipe(
      switchMap((domainId) => {
        const queryParams: Record<string, unknown> = {
          page: params.page ?? DEFAULT_POWER_PAGES_CURRENT_PAGE,
          per_page: params.perPage ?? DEFAULT_POWER_PAGES_PER_PAGE,
          search: params.search ?? '',
          sort: params.sort ?? 'id',
          sort_direction: params.sortDirection ?? 'desc',
        };

        return this.http
          .get<Record<string, unknown>>(`/${domainId}/power-pages`, queryParams)
          .pipe(map((response) => transformSnakeToCamel(response) as PowerPagesListResponse));
      })
    );
  }

  deletePowerPage(uuid: string): Observable<void> {
    return this.getDomainId$().pipe(
      switchMap((domainId) => this.http.delete<void>(`/${domainId}/power-pages/${uuid}`))
    );
  }

  duplicatePowerPage(uuid: string): Observable<{ data?: PowerPage }> {
    return this.getDomainId$().pipe(
      switchMap((domainId) =>
        this.http
          .post<Record<string, unknown>>(`/${domainId}/power-pages/duplicate/${uuid}`)
          .pipe(map((response) => transformSnakeToCamel(response) as { data?: PowerPage }))
      )
    );
  }

  /** Manage-tab options: current domain, admins, managers, social media. */
  getPowerPageOptions(): Observable<PowerPageManageOptions> {
    return this.getDomainId$().pipe(
      switchMap((domainId) =>
        this.http
          .get<Record<string, unknown>>(`/${domainId}/power-pages/manage`)
          .pipe(
            map((response) => {
              const transformed = transformSnakeToCamel(response) as
                | PowerPageManageOptions
                | { data: PowerPageManageOptions };

              return (transformed as { data?: PowerPageManageOptions }).data ?? (transformed as PowerPageManageOptions);
            })
          )
      )
    );
  }

  getPowerPageById(uuid: string): Observable<PowerPageDetail> {
    return this.getDomainId$().pipe(
      switchMap((domainId) =>
        this.http
          .get<Record<string, unknown>>(`/${domainId}/power-pages/${uuid}`)
          .pipe(
            map((response) => {
              const transformed = transformSnakeToCamel(response) as
                | PowerPageDetail
                | { data: PowerPageDetail };

              return (transformed as { data?: PowerPageDetail }).data ?? (transformed as PowerPageDetail);
            })
          )
      )
    );
  }

  createPowerPage(payload: PowerPageSavePayload): Observable<PowerPageDetail> {
    return this.getDomainId$().pipe(
      switchMap((domainId) =>
        this.http
          .post<Record<string, unknown>>(`/${domainId}/power-pages`, this.toApiPayload(payload))
          .pipe(map((response) => (transformSnakeToCamel(response) as { data: PowerPageDetail }).data))
      )
    );
  }

  updatePowerPage(uuid: string, payload: PowerPageSavePayload): Observable<PowerPageDetail> {
    return this.getDomainId$().pipe(
      switchMap((domainId) =>
        this.http
          .patch<Record<string, unknown>>(`/${domainId}/power-pages/${uuid}`, this.toApiPayload(payload))
          .pipe(map((response) => (transformSnakeToCamel(response) as { data: PowerPageDetail }).data))
      )
    );
  }

  /* ---------------------------------------------------------------------- */
  /* Content blocks                                                          */
  /* ---------------------------------------------------------------------- */

  /** GET /{domain}/power-page-blocks/{powerPageUuid} -> FE blocks. */
  getBlocks(powerPageUuid: string): Observable<PowerPageBlock[]> {
    return this.getDomainId$().pipe(
      switchMap((domainId) =>
        this.http
          .get<Record<string, unknown>>(`/${domainId}/power-page-blocks/${powerPageUuid}`)
          .pipe(
            map((response) => {
              const transformed = transformSnakeToCamel(response) as { data?: unknown[] };
              const rows = Array.isArray(transformed.data) ? transformed.data : [];

              return rows.map((row, index) => this.mapApiBlock(row as Record<string, unknown>, index));
            })
          )
      )
    );
  }

  createBlock(powerPageUuid: string, payload: PowerPageBlockSavePayload): Observable<PowerPageBlock> {
    return this.getDomainId$().pipe(
      switchMap((domainId) =>
        this.http
          .post<Record<string, unknown>>(`/${domainId}/power-page-blocks/${powerPageUuid}`, this.toBlockBody(payload))
          .pipe(map((response) => this.mapApiBlock(this.unwrapBlock(response), 0)))
      )
    );
  }

  editBlock(blockUuid: string, payload: PowerPageBlockSavePayload): Observable<PowerPageBlock> {
    return this.getDomainId$().pipe(
      switchMap((domainId) =>
        this.http
          .patch<Record<string, unknown>>(`/${domainId}/power-page-blocks/${blockUuid}`, this.toBlockBody(payload))
          .pipe(map((response) => this.mapApiBlock(this.unwrapBlock(response), 0)))
      )
    );
  }

  deleteBlock(blockUuid: string): Observable<void> {
    return this.getDomainId$().pipe(
      switchMap((domainId) => this.http.delete<void>(`/${domainId}/power-page-blocks/${blockUuid}`))
    );
  }

  /** Wraps the block payload as { blocks: { link, block_settings } }. */
  private toBlockBody(payload: PowerPageBlockSavePayload): Record<string, unknown> {
    return {
      blocks: {
        link: payload.link,
        block_settings: payload.blockSettings,
      },
    };
  }

  private unwrapBlock(response: Record<string, unknown>): Record<string, unknown> {
    const transformed = transformSnakeToCamel(response) as { data?: Record<string, unknown> };

    return transformed.data ?? (transformed as Record<string, unknown>);
  }

  /** Maps a camelCased API block into the editor's FE block shape. */
  private mapApiBlock(block: Record<string, unknown>, index: number): PowerPageBlock {
    const background = block.background as { uuid?: string; url?: string } | null | undefined;

    return {
      link: (block.link ?? {}) as PowerPageBlock['link'],
      blockSettings: {
        uuid: (block.uuid as string) ?? '',
        imageUuid: background?.uuid ?? '',
        background: background?.url ?? '',
        backgroundUrl: background?.url ?? '',
        publishDate: (block.publishedAt as string) ?? '',
        retireDate: (block.retiredAt as string) ?? '',
        text: (block.title as string) ?? '',
        textColor: (block.textColor as string) ?? '#FFFFFF',
        index,
      },
    };
  }

  /**
   * Maps the camelCase save payload to the snake_case API body. Mirrors the
   * React thunk field mapping (domain_uuid, owner, power_page_admins,
   * content_manager, social_medias). `settings` is passed through
   * `transformCamelToSnake` so `isEnabled`/`isMarkup` become snake keys.
   */
  private toApiPayload(payload: PowerPageSavePayload): Record<string, unknown> {
    return {
      status: payload.status,
      path: payload.path,
      name: payload.name,
      description: payload.description ?? '',
      domain_uuid: payload.domainUuid,
      owner: payload.owner,
      power_page_admins: payload.powerPageAdmins ?? [],
      content_manager: payload.contentManager ?? [],
      settings: (payload.settings ?? []).map((setting) => transformCamelToSnake(setting)),
      social_medias: payload.socialMedias ?? [],
    };
  }
}
